import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  consultarStatusAssistente,
  perguntarAoAssistente,
  type ConsultaFeita,
  type FiltrosOficina,
  type MensagemDaConversa,
} from '../../../api/dashboardOficinaApi';
import { notify } from '../../../shared/feedback/notify';
import '../../../shared/feedback/erro-de-tela.css';
// Casco do modal: overlay compartilhado + as classes `do-modal-*` do painel. Import explícito —
// contar com o CSS carregado por outra tela é acoplamento invisível (BUG-V74).
import '../../../components/modal/add-car/AddCarModal.css';
import '../../../shared/ui/dashboard-base.css';
// `filtro-btn-primary` é o botão de ação primária do sistema (já servindo 5 modais por import) —
// reusado aqui em vez de recopiar a especificação dele no CSS do chat.
import '../../../components/modal/contac/filtro/ModalFiltro.css';
import './AssistenteChat.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Chat do assistente do Dashboard Oficina (at-87).
 *
 * ⚠️ A conversa é LOCAL deste componente: fechar o painel a descarta, e nada é persistido no
 * servidor. É o desenho do MVP — sessão guardada traria retenção de dado para uma feature que
 * ainda não provou uso.
 *
 * ⚠️ Toda resposta descreve o RECORTE da tela (os filtros vão no corpo da pergunta), e é por isso
 * que a hint fixa avisa: sem ela, o usuário compara um número do chat com um número de outra aba
 * sem perceber que os recortes podem ser diferentes.
 *
 * ⚠️ Fecha por clique fora e por Escape, e recebe `sidebarRef` porque o fechamento exige clique
 * fora do modal E fora da sidebar — sem a ref, o caminho fica inerte (lição do sentinela de
 * 18/08/2026).
 */

interface Props {
  filtros: FiltrosOficina;
  onFechar: () => void;
  sidebarRef?: React.RefObject<HTMLElement>;
}

interface TurnoExibido extends MensagemDaConversa {
  consultas?: ConsultaFeita[];
  aviso?: string;
}

const ROTULO_FERRAMENTA: Record<string, string> = {
  visao_geral: 'Visão Geral',
  sla_e_tempos: 'SLA e Tempos',
  frota_e_perfil: 'Frota e Perfil',
  regiao: 'Região',
  listar_esteiras: 'Esteiras',
};

/**
 * Perguntas sugeridas, sorteadas a cada abertura do chat (at-88).
 *
 * ⚠️ Toda pergunta aqui precisa ser RESPONDÍVEL pelas ferramentas do assistente — sugestão que
 * termina em "este painel não cobre isso" ensina que o chat não serve, logo no primeiro contato.
 * As quatro primeiras são as originais da at-87; as demais cobrem as abas que ele passou a
 * alcançar, com destaque para as do presente (aba Prazos), que são as que o painel não respondia.
 *
 * Cada uma existe para mostrar um tipo de pergunta diferente: contagem, ranking, "agora",
 * comparação e recorte por atributo. Repetir a mesma forma quatro vezes desperdiçaria o espaço.
 */
const SUGESTOES = [
  // As quatro originais (at-87).
  'Qual bairro tem mais atendimentos?',
  'Quantos veículos estão na oficina agora?',
  'Qual esteira consome mais horas de trabalho?',
  'Quantos veículos estão travados aguardando liberação?',
  // Presente — o que a aba Prazos passou a responder (at-88).
  'O que está estourando prazo agora?',
  'Tem algum veículo parado há muito tempo?',
  'Quantos veículos estão em etapa sem prazo cadastrado?',
  'Qual esteira tem mais veículos atrasados?',
  'Quais veículos estão com o prazo de entrega perto de acabar?',
  // Tempos e equipe.
  'Qual colaborador trabalhou mais horas no período?',
  'Quantas horas estão esperando aprovação do chefe?',
  'Quantos serviços foram deixados inacabados?',
  'Qual etapa demora mais para o veículo, em dias?',
  // Frota e perfil.
  'Que categoria de veículo a oficina mais atende?',
  'Qual a idade média dos veículos atendidos?',
  'Quantos atendimentos são de terceiros?',
  'Qual marca aparece mais na oficina?',
  // Fluxo e retorno.
  'Quantos veículos voltaram para a oficina?',
  'Quantos veículos entraram e saíram no último mês?',
  'De que cidade vêm os veículos atendidos?',
];

/** Quantas sugestões aparecem por vez. Mais que isso e a lista deixa de convidar e vira leitura. */
const SUGESTOES_VISIVEIS = 4;

/**
 * Sorteia sem repetir, com `sort` sobre uma cópia embaralhada.
 *
 * ⚠️ Fora do componente e chamada em `useState(() => ...)`, para o sorteio acontecer UMA vez por
 * abertura: sorteando no corpo do render, as sugestões trocariam a cada tecla digitada no campo.
 */
const sortearSugestoes = (): string[] =>
  [...SUGESTOES].sort(() => Math.random() - 0.5).slice(0, SUGESTOES_VISIVEIS);

const AssistenteChat: React.FC<Props> = ({ filtros, onFechar, sidebarRef }) => {
  const ref = useRef<HTMLDivElement>(null);
  const fimDaConversa = useRef<HTMLDivElement>(null);
  const [turnos, setTurnos] = useState<TurnoExibido[]>([]);
  const [pergunta, setPergunta] = useState('');
  const [aguardando, setAguardando] = useState(false);
  const [indisponivel, setIndisponivel] = useState<string | null>(null);
  const [verificandoStatus, setVerificandoStatus] = useState(true);
  const [sugestoes, setSugestoes] = useState<string[]>(sortearSugestoes);

  useEffect(() => {
    const aoClicarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (ref.current && !ref.current.contains(alvo) && !sidebarRef?.current?.contains(alvo)) {
        onFechar();
      }
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar, sidebarRef]);

  /**
   * Consulta a disponibilidade na abertura.
   *
   * ⚠️ Sem credencial no ambiente, o campo é DESABILITADO com explicação em vez de aceitar a
   * pergunta e falhar — molde do botão da rastreadora (at-76). Falha da própria checagem também
   * desabilita: oferecer o campo sem saber se ele funciona empurra o erro para o usuário.
   */
  useEffect(() => {
    let ativo = true;
    consultarStatusAssistente()
      .then((status) => {
        if (!ativo) return;
        setIndisponivel(
          status.configurado
            ? null
            : 'O assistente não está configurado neste ambiente. Acione a TI para habilitá-lo.',
        );
      })
      .catch((erro) => {
        if (!ativo) return;
        setIndisponivel('Não foi possível verificar se o assistente está disponível.');
        notify.errorFrom(
          erro,
          'Não foi possível verificar se o assistente está disponível. Recarregue a página; se persistir, acione a TI.',
        );
      })
      .finally(() => {
        if (ativo) setVerificandoStatus(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    fimDaConversa.current?.scrollIntoView({ block: 'end' });
  }, [turnos, aguardando]);

  const enviar = useCallback(
    async (texto: string) => {
      const limpo = texto.trim();
      if (!limpo || aguardando || indisponivel) return;

      const conversaAtualizada: TurnoExibido[] = [...turnos, { papel: 'usuario', texto: limpo }];
      setTurnos(conversaAtualizada);
      setPergunta('');
      setAguardando(true);

      try {
        const resposta = await perguntarAoAssistente(
          conversaAtualizada.map(({ papel, texto: t }) => ({ papel, texto: t })),
          filtros,
        );
        setTurnos([
          ...conversaAtualizada,
          {
            papel: 'assistente',
            texto: resposta.texto,
            consultas: resposta.consultas,
            aviso: resposta.aviso,
          },
        ]);
      } catch (erro) {
        /*
          ⚠️ O turno do usuário PERMANECE na tela e o texto volta para o campo: a falha não pode
          apagar a pergunta que ele escreveu. E o erro é sempre visível (regra da at-60) — sem
          toast, a ausência de resposta pareceria o assistente "não ter nada a dizer".
        */
        setTurnos(conversaAtualizada);
        setPergunta(limpo);
        notify.errorFrom(
          erro,
          'O assistente não conseguiu responder. Tente novamente; se persistir, acione a TI.',
        );
      } finally {
        setAguardando(false);
      }
    },
    [aguardando, filtros, indisponivel, turnos],
  );

  const bloqueado = aguardando || verificandoStatus || indisponivel !== null;

  return (
    <div className="modal-overlay">
      <div
        className="do-modal assistente-modal"
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Assistente do Dashboard Oficina"
      >
        <div className="do-modal-header">
          <h2 className="do-modal-titulo">Perguntar ao assistente</h2>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <p className="assistente-hint">
          As respostas saem dos mesmos dados das abas deste painel, no recorte que está aplicado na
          tela — a resposta diz qual recorte usou. O assistente não altera nada e não enxerga
          assuntos fora deste painel.
        </p>

        {indisponivel && <p className="erro-de-tela assistente-indisponivel">{indisponivel}</p>}

        {turnos.length === 0 && !indisponivel && (
          <div className="assistente-sugestoes">
            {sugestoes.map((sugestao) => (
              <button
                key={sugestao}
                type="button"
                className="assistente-sugestao"
                disabled={bloqueado}
                onClick={() => enviar(sugestao)}
              >
                {sugestao}
              </button>
            ))}
            {/*
              Troca as sugestões sem precisar fechar o chat. Fica ao lado, discreto: quem já sabe
              o que perguntar digita e ignora isto.
            */}
            <button
              type="button"
              className="assistente-sugestao assistente-sugestao-trocar"
              disabled={bloqueado}
              onClick={() => setSugestoes(sortearSugestoes())}
              title="Ver outras perguntas"
            >
              Outras perguntas
            </button>
          </div>
        )}

        <div className="assistente-corpo">
          {turnos.map((turno, indice) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`${turno.papel}-${indice}`}>
              <div
                className={`assistente-bolha ${
                  turno.papel === 'usuario'
                    ? 'assistente-bolha-usuario'
                    : 'assistente-bolha-assistente'
                }`}
              >
                {turno.texto}
              </div>
              {turno.aviso && <p className="assistente-aviso">{turno.aviso}</p>}
              {turno.consultas && turno.consultas.length > 0 && (
                <p className="assistente-consultas">
                  Consultei:{' '}
                  {Array.from(
                    new Set(turno.consultas.map((c) => ROTULO_FERRAMENTA[c.ferramenta] ?? c.ferramenta)),
                  ).join(', ')}
                </p>
              )}
            </React.Fragment>
          ))}

          {aguardando && (
            <div
              className="assistente-carregando"
              role="status"
              aria-label="Consultando os dados"
            >
              <span className="assistente-ponto" />
              <span className="assistente-ponto" />
              <span className="assistente-ponto" />
            </div>
          )}
          <div ref={fimDaConversa} />
        </div>

        <div className="assistente-barra">
          <textarea
            className="assistente-campo"
            placeholder={
              indisponivel ? 'Assistente indisponível' : 'Pergunte algo sobre os dados do painel'
            }
            value={pergunta}
            disabled={bloqueado}
            onChange={(evento) => setPergunta(evento.target.value)}
            onKeyDown={(evento) => {
              // Enter envia; Shift+Enter quebra linha — convenção de campo de conversa.
              if (evento.key === 'Enter' && !evento.shiftKey) {
                evento.preventDefault();
                enviar(pergunta);
              }
            }}
          />
          <button
            type="button"
            className="filtro-btn-primary"
            disabled={bloqueado || pergunta.trim() === ''}
            onClick={() => enviar(pergunta)}
          >
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssistenteChat;
